<script setup>
  import {computed, inject, nextTick, reactive, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentUniversalPatientAssignment from "@components/PatientSelect/UniversalPatientAssignment.vue";
  import {router} from "@inertiajs/vue3";
  import {circumstancesCarer, wishesAims} from "@pages/Patientinput/Enums.js";
  import {recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import resetState from "@utils/Helpers/ResetState.js";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";
  import {cloneDeep} from "es-toolkit";

  defineOptions({
    name: "PatientinputClientComponentImportDialog",
  });

  const privacy = inject("$privacy");

  const refUniversalPatientAssignment = ref(null);
  const refDialog = ref(null);

  let cancelCurrentVisit;

  const initialState = {
    status: "loading",
    transfer: null,
    selectedExistingPatient: null,
    nursingHomeData: null,
  };

  const state = reactive({...initialState});

  const dialogTitle = computed(() => {
    return (
      (state.transfer?.meta.is_carer ? recordOriginTexts.carerinput : recordOriginTexts.patientinput) + " importieren"
    );
  });

  const reset = () => {
    for (const [key, value] of Object.entries(initialState)) {
      state[key] = resetState(value);
    }
  };

  /**
   * Convert the given transfer data attributes into
   * the usual patient_attributes data structure.
   */
  async function buildPatientAttributesData() {
    // build "patient_attributes"
    let patient_attributes = [];

    if (!state?.transfer?.data) {
      return patient_attributes;
    }

    // "degreeOfCare" is not included in patient input data

    // "mobility"
    if (
      state.transfer.data?.circumstances?.attributes?.stick ||
      state.transfer.data?.circumstances?.attributes?.rollator ||
      state.transfer.data?.circumstances?.attributes?.wheelchair ||
      state.transfer.data?.circumstances?.attributes?.bedridden
    ) {
      patient_attributes.push({
        type: "mobility",
        payload: {
          stick: !!state.transfer.data?.circumstances?.attributes?.stick,
          rollator: !!state.transfer.data?.circumstances?.attributes?.rollator,
          wheelchair: !!state.transfer.data?.circumstances?.attributes?.wheelchair,
          bedridden: !!state.transfer.data?.circumstances?.attributes?.bedridden,
        },
      });
    }

    // nursingHome
    if (
      state.transfer.data?.masterdata?.nursing_home_name ||
      state.transfer.data?.masterdata?.nursing_home_ward ||
      state.transfer.data?.masterdata?.nursing_home_room ||
      state.transfer.data?.masterdata?.nursing_home_carer
    ) {
      const [name, ward, room, carer] = await Promise.all([
        privacy.encryptValue(state.transfer.data?.masterdata?.nursing_home_name),
        privacy.encryptValue(state.transfer.data?.masterdata?.nursing_home_ward),
        privacy.encryptValue(state.transfer.data?.masterdata?.nursing_home_room),
        privacy.encryptValue(state.transfer.data?.masterdata?.nursing_home_carer),
      ]);

      patient_attributes.push({
        type: "nursingHome",
        payload: {
          nursing_home_name: name,
          nursing_home_ward: ward,
          nursing_home_room: room,
          nursing_home_carer: carer,
        },
      });
    }

    // "patientAddress" is not included into patient input data

    // "tubePatient"
    if (
      state.transfer.data?.circumstances?.attributes?.tubePatient &&
      state.transfer.data?.circumstances?.attributes?.tubePatient?.selected === true
    ) {
      patient_attributes.push({
        type: "tubePatient",
        payload: state.transfer.data?.circumstances?.attributes?.tubePatient?.payload ?? {},
      });
    }

    // "insulinPump", "palliative", "stoma", "port"
    for (const type of ["insulinPump", "palliativePatient", "stoma", "port"]) {
      if (
        state.transfer.data?.circumstances?.attributes &&
        state.transfer.data?.circumstances?.attributes[type] &&
        state.transfer.data?.circumstances?.attributes[type] === true
      ) {
        patient_attributes.push({
          type: type,
          payload: null,
        });
      }
    }

    return patient_attributes;
  }

  const open = async (transfer) => {
    reset();

    state.transfer = cloneDeep(transfer);

    refDialog.value.open();

    state.status = "universalPatientAssignment";

    let sourcePatient = state.transfer.data.masterdata.patient;

    // build patient attributes in correct data structure and
    // add them to sourcePatient (who should be imported)
    sourcePatient.patient_attributes = await buildPatientAttributesData();

    nextTick(() => {
      refUniversalPatientAssignment.value.setPatient(sourcePatient);
    });
  };

  const close = () => {
    refDialog.value?.close();
    refUniversalPatientAssignment.value?.clearPatient();
    pdfRouter.cancelDom?.abort();
    // instead of calling router.cancelAll() [as a replacement for the inertia v2 method
    // router.cancel()], we have to cancel the request manually via token
    cancelCurrentVisit?.();
  };

  const performImport = async (patient, isNewPatient = false) => {
    state.status = "saving";

    const data = {...state.transfer.data, existing_patient_id: isNewPatient ? null : patient?.id};

    data.masterdata.patient = await privacy.encryptPatient(patient);

    const [contactName, contactEmail, contactTelephone] = await Promise.all([
      (data.contactdata?.name ?? "").length > 0 ? privacy.encryptValue(data.contactdata.name) : null,
      (data.contactdata?.email ?? "").length > 0 ? privacy.encryptValue(data.contactdata.email) : null,
      (data.contactdata?.telephone ?? "").length > 0 ? privacy.encryptValue(data.contactdata.telephone) : null,
    ]);

    data.contactdata = {
      name: contactName,
      email: contactEmail,
      telephone: contactTelephone,
    };

    const masterDataAttributes = [
      "doc_name",
      "nursing_home_carer",
      "nursing_home_name",
      "nursing_home_street",
      "nursing_home_zip",
      "nursing_home_city",
      "nursing_home_ward",
      "nursing_home_room",
    ];

    for (const key of masterDataAttributes) {
      if (data?.masterdata[key] ?? null) {
        data.masterdata[key] = await privacy.encryptValue(data.masterdata[key]);
      }
    }

    if (state.transfer.meta.is_carer) {
      const circumstanceAttributes = [];

      for (const key of ["bitesMedicines", "refusesMedications", "swallowingDifficulties"]) {
        if (data?.circumstances?.attributes && data.circumstances?.attributes[key]) {
          const label = circumstancesCarer.find((attribute) => attribute.key === key).label;

          circumstanceAttributes.push(label);
        }
      }

      if (circumstanceAttributes.length > 0) {
        data.info = await privacy.encryptValue("- " + circumstanceAttributes.join("\n- "));
      }
    } else {
      const info = [];

      if (data.wishes_aims?.length > 0) {
        info.push("Wünsche und Ziele des Patienten:\n- " + data.wishes_aims.map((key) => wishesAims[key]).join("\n- "));
      }

      if (data.additional) {
        info.push("Weitere Informationen:\n" + data.additional);
      }

      if (info.length > 0) {
        data.info = await privacy.encryptValue(info.join("\n\n"));
      }
    }

    // Decode BMP

    if (data.medication?.bmp?.length > 0) {
      const decodedBmp = await new Promise((resolve, reject) => {
        pdfRouter.decodeXml(
          {pages: data.medication.bmp},
          {
            onSuccess: (response) => {
              resolve(response.data);
            },
            onError: () => {
              state.status = "error";
              reject();
            },
          },
        );
      });

      delete data.medication.bmp;

      data.medication.bmp_pzns = decodedBmp.record.medications;
      data.medication.failed = decodedBmp.failed ?? [];
    }

    // Save
    router.put(route("patientinput-client.update", {transfer: state.transfer.id}), data, {
      onCancelToken: (token) => {
        cancelCurrentVisit = token.cancel;
      },
      onSuccess: close,
      onError: () => (state.status = "error"),
    });
  };

  const addToExistingPatient = (selectedPatient) => {
    performImport(selectedPatient, false);
  };

  const newPatient = (patient) => {
    performImport(patient, true);
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" :dialog-title="dialogTitle" dialog-width="large" :test-id="getTestId('import')">
    <template #content>
      <template v-if="state.status === 'universalPatientAssignment'">
        <ComponentUniversalPatientAssignment
          ref="refUniversalPatientAssignment"
          @existing-patient-selected="(patient) => addToExistingPatient(patient)"
          @new-patient="(patient) => newPatient(patient)"
        />
      </template>

      <template v-else-if="state.status === 'error'">
        <ComponentAlert type="error" :test-id="getTestId('import-dialog')">
          Es ist ein Fehler aufgetreten. Bitte versuchen Sie den Import erneut.
        </ComponentAlert>
      </template>

      <div v-else-if="state.status === 'saving'" class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Wird gespeichert...</div>
        <MedSpinner size="3xl" :data-test="getTestId('import-dialog-saving-spinner')" />
      </div>

      <div v-else class="flex flex-col items-center justify-center py-10">
        <MedSpinner size="3xl" :data-test="getTestId('import-dialog-loading-spinner')" />
      </div>
    </template>

    <template #actions>
      <!-- <Teleport> target of UniversalPatientAssignment component (reverts space-x margin when empty) -->
      <div id="UniversalPatientAssignmentSearchButton" />

      <MedButton :data-test="getTestId('import-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
